/**
 * TemplateVariables — 템플릿 변수 카탈로그
 *
 * liquidHtml 이 참조하는 변수 사전. 목록의 SSOT 는 DB(template_variables)이고
 * 코드(마커 사전/Zod 스키마/파생값)와의 불일치는 상단 drift 배너가 알려준다.
 *
 * 샘플값은 문서용 표시 텍스트다 — 실제 청첩장 렌더에 주입되지 않는다.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { useApiFetch } from '../hooks/useApiFetch'
import { Alert } from '../ui/Alert'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { AdminTable } from '../ui/AdminTable'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Checkbox } from '../ui/Checkbox'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { EmptyState } from '../ui/EmptyState'
import { Skeleton } from '../ui/Skeleton'
import type { TableColumn } from '../ui/Table'
import styles from './TemplateVariables.module.css'

// ── 타입 ──

export type VariableSource = 'mapper' | 'derived' | 'template-only' | 'dict-only'
export type VariableKind = 'scalar' | 'array' | 'boolean' | 'object'
export type WarningFilter = '' | 'unsaved' | 'deprecated' | 'ghost'
export type FieldType = 'text' | 'textarea' | 'toggle' | 'select' | 'number' | 'repeater'

export interface TemplateVariable {
  id: string
  path: string
  kind: VariableKind
  category: string
  valueType: string | null
  description: string | null
  sampleValue: string | null
  notes: string | null
  source: VariableSource
  schemaBacked: boolean
  editableByUser: boolean
  isDeprecated: boolean
  sortOrder: number
  renderInjected: boolean
  globalValue: unknown
  fieldType: FieldType | null
  fieldLabel: string | null
  placeholder: string | null
  helpText: string | null
  sectionId: string | null
  fieldOptions: unknown
  createdAt: string
  updatedAt: string
}

interface ListResponse {
  items: TemplateVariable[]
}

interface DriftResponse {
  missingInDb: { path: string; kind: VariableKind; origin: 'schema' | 'dict' | 'derived' | 'template' }[]
  missingInCode: { path: string; source: VariableSource }[]
  schemaMismatch: { path: string; dbSchemaBacked: boolean; actual: boolean }[]
}

const ALL_CATEGORY = '__all__'

const SOURCE_BADGE: Record<VariableSource, { color: 'green' | 'blue' | 'yellow' | 'red'; label: string }> = {
  mapper: { color: 'green', label: 'mapper' },
  derived: { color: 'blue', label: 'derived' },
  'template-only': { color: 'yellow', label: 'template-only' },
  'dict-only': { color: 'red', label: 'dict-only' },
}

const SOURCE_OPTIONS = [
  { value: '', label: '출처: 전체' },
  { value: 'mapper', label: 'mapper' },
  { value: 'derived', label: 'derived' },
  { value: 'template-only', label: 'template-only' },
  { value: 'dict-only', label: 'dict-only' },
]

const WARNING_OPTIONS = [
  { value: '', label: '경고: 전체' },
  { value: 'unsaved', label: '미저장 (스키마 없음)' },
  { value: 'deprecated', label: '폐기' },
  { value: 'ghost', label: '유령 (dict-only)' },
]

const FIELD_TYPE_OPTIONS = [
  { value: '', label: '(없음)' },
  { value: 'text', label: 'text — 한 줄 입력' },
  { value: 'textarea', label: 'textarea — 여러 줄' },
  { value: 'toggle', label: 'toggle — 켜기/끄기' },
  { value: 'select', label: 'select — 선택지' },
  { value: 'number', label: 'number — 숫자' },
  { value: 'repeater', label: 'repeater — 반복 (에디터 미구현)' },
]

const RENDER_FILTER_OPTIONS = [
  { value: '', label: '주입: 전체' },
  { value: 'on', label: '주입 ON' },
  { value: 'off', label: '주입 OFF' },
]

/**
 * 에디터 아코디언 섹션 ID — 자유입력이면 오타 한 글자에 입력칸이 조용히 사라진다.
 * 출처: apps/web/src/components/editor/EditorForm.tsx:54 의 ACCORDION_ORDER.
 * apps/admin 은 @fiket/shared 를 쓰지 않는 독립 SPA 라 수동 동기화한다.
 */
const SECTION_ID_OPTIONS = [
  { value: '', label: '(선택 안 함)' },
  { value: 'design', label: 'design — 디자인' },
  { value: 'cover', label: 'cover — 표지' },
  { value: 'greeting', label: 'greeting — 인사말' },
  { value: 'parents', label: 'parents — 혼주' },
  { value: 'ceremony', label: 'ceremony — 예식 일정' },
  { value: 'venue', label: 'venue — 예식 장소' },
  { value: 'directions', label: 'directions — 오시는 길' },
  { value: 'gallery', label: 'gallery — 갤러리' },
  { value: 'timeline', label: 'timeline — 우리의 이야기' },
  { value: 'schedule', label: 'schedule — 일정' },
  { value: 'rsvp', label: 'rsvp — 참석 여부' },
  { value: 'guestbook', label: 'guestbook — 방명록' },
  { value: 'accounts', label: 'accounts — 마음 전하실 곳' },
  { value: 'contacts', label: 'contacts — 연락처' },
  { value: 'notices', label: 'notices — 안내사항' },
  { value: 'wreath', label: 'wreath — 화환' },
  { value: 'ending', label: 'ending — 맺음말' },
  { value: 'bgm', label: 'bgm — 배경음악' },
  { value: 'kakaoShare', label: 'kakaoShare — 카카오 공유' },
  { value: 'settings', label: 'settings — 설정' },
]

const SECTION_IDS = new Set(SECTION_ID_OPTIONS.map((o) => o.value).filter(Boolean))

/**
 * path 루트가 에디터 섹션 이름과 같으면 그 섹션을 기본값으로 본다.
 * gallery.test → gallery. 루트가 섹션명이 아니면 빈 문자열(운영자가 직접 고름).
 */
function inferSectionId(path: string): string {
  const root = path.split('.')[0].trim()
  return SECTION_IDS.has(root) ? root : ''
}

/** 전역 기본값 입력칸에 붙일 안내 — fieldType 마다 허용 형식이 다르다. */
function globalValueHint(fieldType: string): string {
  if (fieldType === 'number') return '숫자만 입력하세요 (예: 3). 숫자가 아니면 비워집니다'
  if (fieldType === 'toggle') return 'true 또는 false'
  if (fieldType === 'select') return '아래 선택지의 value 중 하나'
  return '사용자가 값을 비워두면 이 값이 렌더에 들어갑니다'
}

/**
 * 24키 루트 — 이 루트를 공유하는 카탈로그 변수는 leaf 단위로만 살아남는다.
 * 출처: apps/api/src/lib/templates/variable-sources.ts:25-31 의 MAPPER_SCOPE_ROOTS.
 * apps/admin 은 @fiket/shared 를 쓰지 않는 독립 SPA 라 수동 동기화한다.
 */
const MAPPER_ROOTS = new Set([
  'invitation', 'viewerMode', 'groom', 'bride', 'wedding', 'weddingDate',
  'content', 'gallery', 'cover', 'sectionSubtitles', 'labels', 'timeline',
  'schedule', 'notices', 'bgm', 'video', 'ending', 'share', 'settings',
  'enabledSections', 'wreathOrderUrl', 'wreathDeclined', 'naverMapClientId',
  'naverMapEnabled',
])

/** 24키와의 충돌 유형. 렌더 주입이 켜진 row 에만 의미가 있다. */
function mapperConflict(path: string): 'blocked' | 'shared' | null {
  const root = path.split('.')[0]
  if (!MAPPER_ROOTS.has(root)) return null
  return path.includes('.') ? 'shared' : 'blocked'
}

/** field_options.choices 를 드래프트용으로 안전하게 읽는다. 형식이 깨지면 빈 배열. */
function readChoicesFromRow(fieldOptions: unknown): { value: string; label: string }[] {
  const choices = (fieldOptions as { choices?: unknown } | null | undefined)?.choices
  if (!Array.isArray(choices)) return []
  return choices
    .filter((c): c is { value: string; label: string } =>
      typeof c === 'object' && c !== null
      && typeof (c as { value?: unknown }).value === 'string'
      && typeof (c as { label?: unknown }).label === 'string')
    .map((c) => ({ value: c.value, label: c.label }))
}

/**
 * 전역 기본값 문자열을 fieldType 에 맞는 타입으로 변환한다.
 * global_value 는 jsonb 라 타입이 보존되므로, 숫자를 문자열로 저장하면
 * liquid 에서 숫자 비교·boolean 분기가 깨진다.
 */
function parseGlobalValue(raw: string, fieldType: string): unknown {
  const v = raw.trim()
  if (!v) return null
  if (fieldType === 'number') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  if (fieldType === 'toggle') return v === 'true' || v === '1'
  return v
}

// ── 모달 드래프트 타입·상수 ──

interface DraftVariable {
  id?: string
  path: string
  kind: VariableKind
  category: string
  valueType: string
  source: VariableSource
  description: string
  sampleValue: string
  notes: string
  schemaBacked: boolean
  editableByUser: boolean
  isDeprecated: boolean
  sortOrder: number
  renderInjected: boolean
  fieldType: FieldType | ''
  fieldLabel: string
  placeholder: string
  helpText: string
  sectionId: string
  /** 전역 기본값. 문자열로 입력받아 저장 시 fieldType 에 맞게 변환한다 */
  globalValue: string
  /** select 선택지. fieldType === 'select' 일 때만 쓴다 */
  choices: { value: string; label: string }[]
  /** 운영자가 섹션을 직접 고른 적이 있으면 path 로부터 추론하지 않는다 */
  sectionTouched: boolean
}

const EMPTY_DRAFT: DraftVariable = {
  path: '', kind: 'scalar', category: '기타', valueType: '', source: 'mapper',
  description: '', sampleValue: '', notes: '',
  schemaBacked: false, editableByUser: false, isDeprecated: false, sortOrder: 0,
  renderInjected: false, fieldType: '', fieldLabel: '', placeholder: '',
  helpText: '', sectionId: '', globalValue: '', choices: [], sectionTouched: false,
}

const KIND_OPTIONS = [
  { value: 'scalar', label: 'scalar' },
  { value: 'array', label: 'array' },
  { value: 'boolean', label: 'boolean' },
  { value: 'object', label: 'object' },
]

const SOURCE_FORM_OPTIONS = [
  { value: 'mapper', label: 'mapper' },
  { value: 'derived', label: 'derived' },
  { value: 'template-only', label: 'template-only' },
  { value: 'dict-only', label: 'dict-only' },
]

/**
 * EditableCell — 셀 클릭 시 그 자리에서 입력창으로 전환.
 * Enter 또는 blur 에 저장, Esc 취소. 저장 실패 시 원값으로 복귀한다.
 * 낙관적 갱신은 하지 않는다 — 저장 성공 후 부모가 해당 row 만 병합한다.
 */
function EditableCell({
  value,
  placeholder,
  onSave,
}: {
  value: string | null
  placeholder: string
  onSave: (next: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  // Enter·blur·Esc 가 겹쳐서 PATCH 가 두 번 발행되거나 Esc 취소 후 blur 가 저장을 덮어쓰는 것을 막는다.
  // state 대신 ref 를 쓰는 이유: ref 는 동기적이라 stale closure 문제가 없다.
  const settled = useRef(false)

  const begin = () => {
    settled.current = false
    setDraft(value ?? '')
    setEditing(true)
  }

  const commit = async () => {
    if (settled.current) return
    settled.current = true
    const next = draft.trim()
    if (next === (value ?? '')) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch {
      // 부모가 토스트를 띄운다. 여기선 원값 복귀만.
      setDraft(value ?? '')
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <input
        className={styles.editInput}
        autoFocus
        disabled={saving}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); void commit() }
          if (e.key === 'Escape') {
            e.preventDefault()
            settled.current = true  // 이후 blur 가 commit 을 호출해도 no-op
            setEditing(false)
          }
        }}
      />
    )
  }

  return (
    <span
      className={`${styles.editableCell} ${value ? '' : styles.editablePlaceholder}`}
      role="button"
      tabIndex={0}
      title="클릭하면 편집"
      onClick={begin}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); begin() } }}
    >
      {value || placeholder}
    </span>
  )
}

export function TemplateVariables() {
  const { addToast } = useToast()

  // 폐기 항목까지 전량 받아 화면에서 필터한다 (300건 규모라 서버 왕복이 불필요)
  const { data, isLoading, error, refetch, mutate } = useApiFetch<ListResponse>(
    '/api/admin/template-variables?includeDeprecated=true',
  )
  // mergeRow 에서 항상 최신 items 를 참조하기 위한 ref.
  // mutate 는 함수형 업데이트를 지원하지 않으므로 ref 로 스냅샷 문제를 우회한다.
  const itemsRef = useRef<TemplateVariable[]>([])
  itemsRef.current = data?.items ?? []

  // 목록과 별개로 drift 로드 — 배너 조건부 렌더에만 쓴다
  const { data: drift } = useApiFetch<DriftResponse>('/api/admin/template-variables/drift')
  const [driftOpen, setDriftOpen] = useState(false)

  const driftTotal = drift
    ? drift.missingInDb.length + drift.missingInCode.length + drift.schemaMismatch.length
    : 0

  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<'' | VariableSource>('')
  const [warningFilter, setWarningFilter] = useState<WarningFilter>('')
  const [renderFilter, setRenderFilter] = useState<'' | 'on' | 'off'>('')
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY)

  const items = data?.items ?? []

  // ── 필터 (카테고리 제외 — 내비 개수 계산에 카테고리 필터는 빼야 한다) ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (q && !it.path.toLowerCase().includes(q)
        && !(it.description ?? '').toLowerCase().includes(q)) return false
      if (sourceFilter && it.source !== sourceFilter) return false
      if (warningFilter === 'unsaved' && it.schemaBacked) return false
      if (warningFilter === 'deprecated' && !it.isDeprecated) return false
      if (warningFilter === 'ghost' && it.source !== 'dict-only') return false
      if (renderFilter === 'on' && !it.renderInjected) return false
      if (renderFilter === 'off' && it.renderInjected) return false
      return true
    })
  }, [items, search, sourceFilter, warningFilter, renderFilter])

  // ── 카테고리 목록 + 개수 ──
  const categories = useMemo(() => {
    const counts = new Map<string, number>()
    for (const it of filtered) counts.set(it.category, (counts.get(it.category) ?? 0) + 1)
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ko'))
  }, [filtered])

  const visible = useMemo(
    () => (activeCategory === ALL_CATEGORY
      ? filtered
      : filtered.filter((it) => it.category === activeCategory)),
    [filtered, activeCategory],
  )

  // 전체 선택 시 카테고리 소제목으로 끊어 렌더
  const groups = useMemo(() => {
    if (activeCategory !== ALL_CATEGORY) return [{ category: '', rows: visible }]
    const map = new Map<string, TemplateVariable[]>()
    for (const it of visible) {
      const arr = map.get(it.category)
      if (arr) arr.push(it)
      else map.set(it.category, [it])
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'ko'))
      .map(([category, rows]) => ({ category, rows }))
  }, [visible, activeCategory])

  /** 서버 응답 row 하나를 로컬 목록에 병합 — 전체 refetch 의 스크롤 튐을 피한다.
   * itemsRef.current 를 기준으로 다음 배열을 만들어 동시 편집 시 stale closure 를 방지한다. */
  const mergeRow = useCallback((row: TemplateVariable) => {
    mutate({ items: itemsRef.current.map((it) => (it.id === row.id ? row : it)) })
  }, [mutate])

  const copyPath = useCallback(async (path: string) => {
    try {
      await navigator.clipboard.writeText(`{{ ${path} }}`)
      addToast({ type: 'success', message: `{{ ${path} }} 복사됨` })
    } catch {
      addToast({ type: 'error', message: '클립보드 복사에 실패했습니다' })
    }
  }, [addToast])

  /** 단일 필드 PATCH — 인라인 편집 경로. 성공 시 해당 row 만 로컬 병합한다. */
  const saveField = useCallback(async (
    row: TemplateVariable,
    field: 'description' | 'sampleValue',
    next: string,
  ) => {
    try {
      const updated = await apiFetch<TemplateVariable>(`/api/admin/template-variables/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: next === '' ? null : next }),
      })
      mergeRow(updated)
    } catch (err) {
      addToast({ type: 'error', message: (err as Error).message })
      throw err   // EditableCell 이 원값으로 복귀하도록 재던진다
    }
  }, [mergeRow, addToast])

  // ── 모달 상태 및 핸들러 ──

  const [editing, setEditing] = useState<DraftVariable | null>(null)
  const [submitting, setSubmitting] = useState(false)

  /** drift 배너의 "이 변수 추가" 도 이 함수로 프리필해 모달을 연다. */
  const openCreate = useCallback((prefill?: Partial<DraftVariable>) => {
    const draft = { ...EMPTY_DRAFT, ...prefill }
    // drift 배너에서 path 를 물고 들어오는 경로 — 여기서도 섹션을 추론해준다
    if (!draft.sectionId) draft.sectionId = inferSectionId(draft.path)
    setEditing(draft)
  }, [])

  const openEdit = useCallback((row: TemplateVariable) => {
    setEditing({
      id: row.id,
      path: row.path,
      kind: row.kind,
      category: row.category,
      valueType: row.valueType ?? '',
      source: row.source,
      description: row.description ?? '',
      sampleValue: row.sampleValue ?? '',
      notes: row.notes ?? '',
      schemaBacked: row.schemaBacked,
      editableByUser: row.editableByUser,
      isDeprecated: row.isDeprecated,
      sortOrder: row.sortOrder,
      renderInjected: row.renderInjected,
      fieldType: row.fieldType ?? '',
      fieldLabel: row.fieldLabel ?? '',
      placeholder: row.placeholder ?? '',
      helpText: row.helpText ?? '',
      sectionId: row.sectionId ?? inferSectionId(row.path),
      globalValue: row.globalValue == null ? '' : String(row.globalValue),
      choices: readChoicesFromRow(row.fieldOptions),
      // 이미 저장된 섹션은 운영자의 선택이므로 path 수정에 덮이지 않게 한다
      sectionTouched: !!row.sectionId,
    })
  }, [])

  const handleSave = useCallback(async () => {
    if (!editing) return
    if (!editing.path.trim() || !editing.category.trim()) {
      addToast({ type: 'error', message: 'path 와 카테고리는 필수입니다' })
      return
    }
    if (editing.renderInjected && editing.editableByUser && !editing.sectionId.trim()) {
      addToast({ type: 'error', message: '사용자 입력 변수는 섹션 ID 가 있어야 에디터에 표시됩니다' })
      return
    }
    // 입력 타입이 없으면 에디터가 어떤 입력칸을 그릴지 몰라 조용히 건너뛴다
    if (editing.renderInjected && editing.editableByUser && !editing.fieldType) {
      addToast({ type: 'error', message: '사용자 입력 변수는 입력 타입이 있어야 에디터에 표시됩니다' })
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        path: editing.path.trim(),
        kind: editing.kind,
        category: editing.category.trim(),
        valueType: editing.valueType.trim() || null,
        source: editing.source,
        description: editing.description.trim() || null,
        sampleValue: editing.sampleValue.trim() || null,
        notes: editing.notes.trim() || null,
        schemaBacked: editing.schemaBacked,
        editableByUser: editing.editableByUser,
        isDeprecated: editing.isDeprecated,
        sortOrder: editing.sortOrder,
        renderInjected: editing.renderInjected,
        fieldType: editing.fieldType || null,
        fieldLabel: editing.fieldLabel.trim() || null,
        placeholder: editing.placeholder.trim() || null,
        helpText: editing.helpText.trim() || null,
        sectionId: editing.sectionId.trim() || null,
        globalValue: parseGlobalValue(editing.globalValue, editing.fieldType),
        fieldOptions: editing.fieldType === 'select'
          ? { choices: editing.choices.filter((c) => c.value.trim() && c.label.trim()) }
          : null,
      }
      if (editing.id) {
        // 편집: mergeRow 로 해당 row 만 갱신 — 전체 refetch 의 스크롤 튐을 피한다.
        // category 가 바뀌면 내비 그룹이 달라지지만 visible 메모가 즉시 재계산되므로 문제없다.
        const updated = await apiFetch<TemplateVariable>(
          `/api/admin/template-variables/${editing.id}`,
          { method: 'PATCH', body: JSON.stringify(payload) },
        )
        mergeRow(updated)
        addToast({ type: 'success', message: '변수가 수정되었습니다' })
      } else {
        // 생성: 새 row 가 추가되므로 전체 refetch 로 목록을 갱신한다.
        await apiFetch('/api/admin/template-variables', {
          method: 'POST', body: JSON.stringify(payload),
        })
        refetch()
        addToast({ type: 'success', message: '변수가 추가되었습니다' })
      }
      setEditing(null)
    } catch (err) {
      addToast({ type: 'error', message: (err as Error).message })
    } finally {
      setSubmitting(false)
    }
  }, [editing, refetch, mergeRow, addToast])

  const handleDelete = useCallback(async (row: TemplateVariable) => {
    await apiFetch(`/api/admin/template-variables/${row.id}`, { method: 'DELETE' })
    refetch()
  }, [refetch])

  const columns: TableColumn<TemplateVariable>[] = [
    {
      key: 'path',
      label: 'path',
      width: '24%',
      render: (row) => (
        <button type="button" className={styles.pathButton} onClick={() => copyPath(row.path)} title="클릭하면 {{ path }} 복사">
          {row.path}
        </button>
      ),
    },
    { key: 'kind', label: 'kind', width: '80px', render: (row) => row.kind },
    {
      key: 'description',
      label: '설명',
      width: '22%',
      render: (row) => (
        <EditableCell
          value={row.description}
          placeholder="설명 입력"
          onSave={(next) => saveField(row, 'description', next)}
        />
      ),
    },
    {
      key: 'sampleValue',
      label: '샘플값',
      width: '18%',
      render: (row) => (
        <EditableCell
          value={row.sampleValue}
          placeholder="샘플값 입력"
          onSave={(next) => saveField(row, 'sampleValue', next)}
        />
      ),
    },
    {
      key: 'renderInjected',
      label: '주입',
      width: '70px',
      render: (row) => (row.renderInjected
        ? <Badge color="green">ON</Badge>
        : <span style={{ color: 'var(--color-text-secondary)' }}>OFF</span>),
    },
    { key: 'fieldType', label: '입력타입', width: '90px', render: (row) => row.fieldType ?? '—' },
    {
      key: 'source',
      label: '출처',
      width: '120px',
      render: (row) => {
        const badge = SOURCE_BADGE[row.source]
        return <Badge color={badge.color}>{badge.label}</Badge>
      },
    },
    {
      key: 'warnings',
      label: '경고',
      width: '130px',
      render: (row) => {
        const conflict = row.renderInjected ? mapperConflict(row.path) : null
        return (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {!row.schemaBacked && <Badge color="yellow">미저장</Badge>}
            {row.isDeprecated && <Badge color="red">폐기</Badge>}
            {conflict === 'blocked' && <Badge color="red">24키 충돌</Badge>}
            {conflict === 'shared' && <Badge color="yellow">24키 루트 공유</Badge>}
          </span>
        )
      },
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>템플릿 변수</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: 13 }}>
          liquidHtml 이 참조하는 변수 사전입니다. 샘플값은 문서용 표시 텍스트이며 렌더에 주입되지 않습니다.
          실제로 주입되는 것은 주입 ON + 전역 기본값이며, 사용자가 에디터에서 입력하면 그 값이 우선합니다.
        </p>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          size="sm"
          placeholder="path · 설명 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          size="sm"
          options={SOURCE_OPTIONS}
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as '' | VariableSource)}
        />
        <Select
          size="sm"
          options={WARNING_OPTIONS}
          value={warningFilter}
          onChange={(e) => setWarningFilter(e.target.value as WarningFilter)}
        />
        <Select
          size="sm"
          options={RENDER_FILTER_OPTIONS}
          value={renderFilter}
          onChange={(e) => setRenderFilter(e.target.value as '' | 'on' | 'off')}
        />
        <Button size="sm" onClick={() => openCreate()}>
          <Plus size={14} /> 변수 추가
        </Button>
      </div>

      {driftTotal > 0 && drift && (
        <Alert variant="warning" title="코드 대조 결과" className={styles.driftAlert}>
          코드에 새 변수 {drift.missingInDb.length}개 · DB에만 있는 변수 {drift.missingInCode.length}개 ·
          스키마 불일치 {drift.schemaMismatch.length}개
          {' '}
          <button type="button" className={styles.driftToggle} onClick={() => setDriftOpen((v) => !v)}>
            {driftOpen ? '접기' : '펼쳐보기'}
          </button>
          {driftOpen && (
            <ul className={styles.driftList}>
              {drift.missingInDb.map((d) => (
                <li key={`db-${d.path}`} className={styles.driftRow}>
                  <code>{d.path}</code>
                  <span>코드({d.origin})에만 있음</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openCreate({
                      path: d.path,
                      kind: d.kind,
                      source: d.origin === 'derived' ? 'derived'
                        : d.origin === 'dict' ? 'dict-only'
                        : 'mapper',
                      schemaBacked: d.origin === 'schema',
                      isDeprecated: d.origin === 'dict',
                    })}
                  >
                    이 변수 추가
                  </Button>
                </li>
              ))}
              {drift.missingInCode.map((d) => (
                <li key={`code-${d.path}`}>
                  <code>{d.path}</code> — DB에만 있음 (source: {d.source})
                </li>
              ))}
              {drift.schemaMismatch.map((d) => (
                <li key={`mis-${d.path}`}>
                  <code>{d.path}</code> — schema_backed 표기 {String(d.dbSchemaBacked)} / 실제 {String(d.actual)}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <div className={styles.body}>
        <nav className={styles.nav}>
          <button
            type="button"
            className={`${styles.navItem} ${styles.navItemAll} ${activeCategory === ALL_CATEGORY ? styles.navItemActive : ''}`}
            onClick={() => setActiveCategory(ALL_CATEGORY)}
          >
            전체 <span className={styles.navCount}>{filtered.length}</span>
          </button>
          {categories.map(([category, count]) => (
            <button
              key={category}
              type="button"
              className={`${styles.navItem} ${activeCategory === category ? styles.navItemActive : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category} <span className={styles.navCount}>{count}</span>
            </button>
          ))}
        </nav>

        <div className={styles.content}>
          {isLoading ? (
            // 첫 로드 — AdminTable 은 group 이 없으면 아무것도 안 그리므로 Skeleton 을 직접 띄운다
            <Skeleton lines={8} />
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : visible.length === 0 ? (
            <EmptyState title="조건에 맞는 변수가 없습니다" description="검색어나 필터를 조정해 보세요." />
          ) : (
            groups.map((group) => (
              <div key={group.category || 'single'}>
                {group.category && (
                  <div className={styles.categoryHeading}>
                    {group.category} · {group.rows.length}
                  </div>
                )}
                <AdminTable
                  data={group.rows}
                  columns={columns}
                  rowKey={(r) => r.id}
                  emptyMessage="변수가 없습니다"
                  actions={[{ label: '편집', onClick: (row) => openEdit(row) }]}
                  delete={{
                    message: (row) => `${row.path} 변수를 삭제할까요? 되돌릴 수 없습니다.`,
                    onDelete: handleDelete,
                    successMessage: '변수가 삭제되었습니다',
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {editing && (
        <Modal
          open
          onClose={() => setEditing(null)}
          title={editing.id ? `변수 편집 — ${editing.path}` : '변수 추가'}
          size="lg"
        >
          {/* 1. 기본 — 어떤 변수인가 */}
          <div className={styles.formGrid}>
            <Input
              className={styles.formFull}
              size="sm"
              label="path — liquid 에서 쓸 이름"
              required
              placeholder="footerNote / gallery.perRow"
              helperText="템플릿에서 {{ path }} 로 참조합니다. 영문·숫자·밑줄과 점만"
              value={editing.path}
              onChange={(e) => {
                const path = e.target.value
                // 운영자가 섹션을 직접 고르기 전까지는 path 루트를 따라간다
                setEditing({
                  ...editing,
                  path,
                  sectionId: editing.sectionTouched ? editing.sectionId : inferSectionId(path),
                })
              }}
            />
            <Input
              size="sm"
              label="카테고리 — 목록 분류용"
              required
              value={editing.category}
              onChange={(e) => setEditing({ ...editing, category: e.target.value })}
            />
            <Input
              size="sm"
              label="설명"
              placeholder="이 변수가 무엇인지"
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
          </div>

          {/* 2. 렌더 주입 — 켜야 아래 항목이 열린다 */}
          <div className={styles.sectionDivider}>
            <Checkbox
              label="렌더 주입 — 켜면 청첩장 렌더에 실제로 주입됩니다"
              checked={editing.renderInjected}
              onChange={(v) => setEditing({ ...editing, renderInjected: v })}
            />
            <p className={styles.sectionHint}>
              끄면 이 변수는 문서로만 남습니다 (렌더에 아무 영향 없음).
            </p>
          </div>

          {editing.renderInjected && (
            <div className={styles.formGrid}>
              <Input
                className={styles.formFull}
                size="sm"
                label="전역 기본값 — 렌더에 주입되는 값"
                helperText={globalValueHint(editing.fieldType)}
                value={editing.globalValue}
                onChange={(e) => setEditing({ ...editing, globalValue: e.target.value })}
              />
              <div className={styles.formFull}>
                <Checkbox
                  label="사용자 입력 값 — 켜면 에디터에 입력칸이 생깁니다"
                  checked={editing.editableByUser}
                  onChange={(v) => setEditing({ ...editing, editableByUser: v })}
                />
                <p className={styles.sectionHint}>
                  끄면 전역 기본값만 쓰입니다 (사용자는 바꿀 수 없음).
                </p>
              </div>

              {editing.editableByUser && (
                <>
                  <Select
                    size="sm"
                    label="입력 타입 — 에디터에 그릴 입력칸"
                    options={FIELD_TYPE_OPTIONS}
                    value={editing.fieldType}
                    onChange={(e) => setEditing({ ...editing, fieldType: e.target.value as FieldType | '' })}
                  />
                  <div>
                    <Select
                      size="sm"
                      label="섹션 — 붙일 에디터 아코디언"
                      options={SECTION_ID_OPTIONS}
                      value={editing.sectionId}
                      onChange={(e) => setEditing({ ...editing, sectionId: e.target.value, sectionTouched: true })}
                    />
                    {!editing.sectionTouched && editing.sectionId && (
                      <p className={styles.inferredHint}>
                        path 루트로 자동 선택됨 — 다른 섹션에 붙이려면 바꾸세요
                      </p>
                    )}
                  </div>
                  <Input
                    size="sm"
                    label="입력칸 라벨"
                    placeholder="한 줄당 사진 수"
                    helperText="비우면 path 가 그대로 보입니다"
                    value={editing.fieldLabel}
                    onChange={(e) => setEditing({ ...editing, fieldLabel: e.target.value })}
                  />
                  <Input
                    size="sm"
                    label="placeholder"
                    placeholder="입력칸이 비었을 때 보일 회색 글씨"
                    value={editing.placeholder}
                    onChange={(e) => setEditing({ ...editing, placeholder: e.target.value })}
                  />
                  <Textarea
                    className={styles.formFull}
                    label="입력칸 힌트 — 입력칸 아래 안내문"
                    rows={2}
                    value={editing.helpText}
                    onChange={(e) => setEditing({ ...editing, helpText: e.target.value })}
                  />
                  {editing.fieldType === 'select' && (
                    <div className={styles.formFull}>
                      <div className={styles.choicesLabel}>선택지</div>
                      {editing.choices.map((c, i) => (
                        <div key={i} className={styles.choiceRow}>
                          <Input
                            size="sm"
                            placeholder="value (렌더될 값)"
                            value={c.value}
                            onChange={(e) => {
                              const choices = [...editing.choices]
                              choices[i] = { ...c, value: e.target.value }
                              setEditing({ ...editing, choices })
                            }}
                          />
                          <Input
                            size="sm"
                            placeholder="label (사용자에게 보임)"
                            value={c.label}
                            onChange={(e) => {
                              const choices = [...editing.choices]
                              choices[i] = { ...c, label: e.target.value }
                              setEditing({ ...editing, choices })
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing({ ...editing, choices: editing.choices.filter((_, j) => j !== i) })}
                          >
                            삭제
                          </Button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditing({ ...editing, choices: [...editing.choices, { value: '', label: '' }] })}
                      >
                        선택지 추가
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* 3. 문서·분류 — 렌더 동작과 무관. 기본 접힘 */}
          <details className={styles.advanced}>
            <summary className={styles.advancedSummary}>문서·분류 (렌더에 영향 없음)</summary>
            <div className={styles.formGrid}>
              <Select
                size="sm"
                label="kind"
                options={KIND_OPTIONS}
                value={editing.kind}
                onChange={(e) => setEditing({ ...editing, kind: e.target.value as VariableKind })}
              />
              <Input
                size="sm"
                label="값 타입"
                placeholder="string / number / url / date / enum"
                value={editing.valueType}
                onChange={(e) => setEditing({ ...editing, valueType: e.target.value })}
              />
              <Select
                size="sm"
                label="출처"
                options={SOURCE_FORM_OPTIONS}
                value={editing.source}
                onChange={(e) => setEditing({ ...editing, source: e.target.value as VariableSource })}
              />
              <Input
                size="sm"
                type="number"
                label="정렬 순서"
                value={String(editing.sortOrder)}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) || 0 })}
              />
              <Input
                className={styles.formFull}
                size="sm"
                label="샘플값 (문서용 — 렌더에 주입되지 않음)"
                value={editing.sampleValue}
                onChange={(e) => setEditing({ ...editing, sampleValue: e.target.value })}
              />
              <Textarea
                className={styles.formFull}
                label="운영 메모"
                rows={2}
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
              <div className={`${styles.checkRow} ${styles.formFull}`}>
                <Checkbox
                  label="Zod 스키마에 있음 (schema_backed)"
                  checked={editing.schemaBacked}
                  onChange={(v) => setEditing({ ...editing, schemaBacked: v })}
                />
                <Checkbox
                  label="폐기"
                  checked={editing.isDeprecated}
                  onChange={(v) => setEditing({ ...editing, isDeprecated: v })}
                />
                {/* 렌더 주입이 켜지면 위 블록에서 편집한다 — 여기선 문서용 플래그로만 노출 */}
                {!editing.renderInjected && (
                  <Checkbox
                    label="사용자 입력 값"
                    checked={editing.editableByUser}
                    onChange={(v) => setEditing({ ...editing, editableByUser: v })}
                  />
                )}
              </div>
            </div>
          </details>
          <div className={styles.modalFooter}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(null)} disabled={submitting}>
              취소
            </Button>
            <Button size="sm" onClick={handleSave} loading={submitting}>
              저장
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
